import {Injectable, OnDestroy} from '@angular/core';

import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  callPool,
  callPoolFeatured,
  callPoolsHeadline,
  callPoolsMenu,
  clearCurrentLines,
  failPools,
  partialUpdate,
  setPoolsExtraDetails,
  successLeftMenu,
  successPool,
  successPoolFeatured,
  successPoolsHeadline,
  successPoolsMenu,
  toggleSelection,
  callSportsStats,
  callFantasyStats,
  successSportsStats,
  successFantasyStats
} from './pool.actions';

import {EMPTY, of, Subject} from 'rxjs';
import {catchError, delay, exhaustMap, filter, map, switchMap, takeUntil, tap, withLatestFrom} from 'rxjs/operators';

import {Config} from "../../services/config/config.service";
import {PoolClient} from "../../client/pool";
import {AppState, RouterStateUrl, successCoreSettings} from '../core.state';
import {Store} from '@ngrx/store';
import {selectConfiguration, selectConfigurationState} from '../configuration/configuration.selectors';
import {parseMenu, parsePoolsComplete} from './parsePools';
import {PoolCategories, PoolScope, PoolsMenuScope, SportsStats, FantasyStats} from './pool.models';
import {routerNavigatedAction, routerNavigationAction} from "@ngrx/router-store";
import {setDisplayState} from '../configuration/configuration.actions';
import {selectCurrentPool, selectPools} from './pool.selectors';
import {ws} from "./pool.reducer";
import {setWebsocketEvent} from '../coreWebsockets/coreWebsockets.actions';
import {LocalStorageService} from '../meta-reducers/local-storage.service';
import {loadList} from '../syndicates/syndicates.actions';
import {FantasyStatsExternalClient} from "../../client/fantasy-stats-external";
import {callSportEvents} from "../sport-events/sport-events.actions";
import {getSoftCheckState} from "../ui-states/ui.selectors";

const MICROSITE_POOLS_STATE_KEY = 'micrositePools';

@Injectable()
export class MicrositePoolsEffects implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  routerNavigationPool = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigationAction),
      filter((routerProps) => !(routerProps.payload.routerState as unknown as RouterStateUrl).wallet),
      filter(propVal => propVal.payload.event.url.includes('/pools') || propVal.payload.event.url.includes('/syndicates/create')),
      map(propVal => (propVal.payload.routerState as unknown as RouterStateUrl)?.params?.pool_id),
      filter(poolId => !!poolId),
      withLatestFrom(this.store.select(selectCurrentPool)),
      // Skip callPool if navigating to the same pool — happens when closing the wallet outlet
      // while on a pool page. The wallet close fires routerNavigationAction with the same
      // pool_id but is not a genuine pool navigation; the session refresh via MICROSITE_AUTHENTICATION_REFRESH
      // handles pool data updates independently.
      filter(([poolId, currentPool]) => !currentPool?.id || Number(poolId) !== Number(currentPool.id)),
      takeUntil(this.destroy$),
      switchMap(([poolId]) => {
        const poolIdNum = parseInt(poolId as string, 10);
        return [
          callPool({id: poolId as string}),
          loadList({model: {filters: {pool_id: poolIdNum}}})
        ];
      }))
  }, {dispatch: true});

  callPool = createEffect(() => {
    return this.actions$.pipe(
      ofType(callPool),
      takeUntil(this.destroy$),
      switchMap((propVal) => {
        // Convert string to number if needed
        const poolId = typeof propVal.id === 'string' ? parseInt(propVal.id, 10) : propVal.id;

        return this._poolClient.get(poolId)
          .pipe(
            map((data: PoolScope.CurrentPool) => {
              // KEPT FOR COMPATIBILITY
              if (data.type_code === 'FANTASY_MVP' || data.type_code === 'MVP_FANTASY') {
                this.store.dispatch(callSportEvents({id: propVal.id}));
              }
              if (data.sport_code === 'FANTASY_GAMES' && propVal.shouldLoadStats !== false) {
                this.statsPreload(data);
              }
              return successPool({pool: data});
            }),
            catchError(error => {
              return of(failPools({error: error}))
            })
          )
      }))
  }, {dispatch: true});

  toggleSelection = createEffect(() => {
    return this.actions$.pipe(
      ofType(toggleSelection),
      withLatestFrom(this.store.select(selectPools)),
      takeUntil(this.destroy$),
      tap(([propVal, poolsState]) => {
        // save to localstorage
        // Note: currentGTSelections is now in the GroupTickets feature state.
        // GT selections persistence is managed by the GroupTickets feature reducer.
        this.ngrxLocalStorage.setItem(MICROSITE_POOLS_STATE_KEY, {
          currentPoolSelections: poolsState.currentPoolSelections
        });
      }))
  }, {dispatch: false});


  clearCurrentLines = createEffect(() => {
    return this.actions$.pipe(
      ofType(clearCurrentLines),
      withLatestFrom(this.store.select(selectPools)),
      takeUntil(this.destroy$),
      tap(([propVal, poolsState]) => {
        // save to local storage
        this.ngrxLocalStorage.setItem(MICROSITE_POOLS_STATE_KEY, {currentPoolSelections: poolsState.currentPoolSelections});
      }))
  }, {dispatch: false});

  callPoolFeatured = createEffect(() => {
    return this.actions$.pipe(
      ofType(callPoolFeatured),
      takeUntil(this.destroy$),
      exhaustMap((propVal) => {
        return this._poolClient.featured()
          .pipe(
            map((data: PoolScope.PoolFeatured) => {
              return successPoolFeatured({featuredPoolResponse: data});
            }),
            catchError(error => {
              return of(failPools({error: error}))
            })
          )
      }))
  }, {dispatch: true});

  callPoolsMenu = createEffect(() => {
    return this.actions$.pipe(
      ofType(callPoolsMenu),
      withLatestFrom(this.store.select(selectConfiguration), this.store.select(selectPools), this.store.select(getSoftCheckState)),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate concurrent menu requests
      exhaustMap(([propVal, configuration, pools, us_state]) => {
        return this._poolClient.getMenu()
          .pipe(
            switchMap((data: PoolsMenuScope.Response) => {
              let filteredPools: PoolsMenuScope.Response = data;
              try {
                filteredPools = this.filterMenuByState(data,us_state);
              } catch(error) {
                //
              }
              // Pre-existing assumption: coreSettings/localSettings are populated whenever the pools menu loads — not independently verified
              const { menu, filters, extra } = parsePoolsComplete(
                filteredPools.pools,
                configuration.coreSettings!,
                configuration.localSettings!,
                pools.poolsExtraModel
              );
              const parsedLeftMenu = parseMenu(menu);

              return [
                successPoolsMenu({parsedPoolsMenu: menu}),
                successLeftMenu({parsedLeftMenu: parsedLeftMenu}),
                setPoolsExtraDetails({poolsExtraModel: extra, filters: filters}),
              ];
            }),
            catchError(error => {
              console.error('[Pool Effects] Failed to load pools menu:', error);
              // TODO: Normalize error using normalizeError() once backend error format is confirmed
              return of(failPools({error}))
            })
          );
      })
    );
  }, {dispatch: true});

  callPoolsHeadline = createEffect(() => {
    return this.actions$.pipe(
      ofType(callPoolsHeadline),
      withLatestFrom(this.store.select(selectConfigurationState)),
      takeUntil(this.destroy$),
      // delay(10),
      exhaustMap(([propVal, configuration]) => {
        return this._poolClient.headlines()
          .pipe(
            map((data: PoolsMenuScope.PoolHeadlineItem[]) => {
              return successPoolsHeadline({poolsHeadlineResponse: data, coreSettings: configuration.coreSettings});
            }),
            catchError(error => {
              return of(failPools({error: error}))
            })
          )
      }))
  }, {dispatch: true});

  routerNavigatedAction = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigatedAction),
      filter(propVal => propVal.payload.event.url.includes('/pools')),
      map(propVal => {
        return propVal.payload.event.url.split('/')[2]?.toUpperCase()
      }),
      filter(view => (isNaN(Number(view)) || isNaN(parseFloat(view)))),
      filter(view => (view in PoolCategories)),
      delay(1000),
      takeUntil(this.destroy$),
      exhaustMap((view) => {

        // might cause problems in other paths which contain '/pools'
        return of(setDisplayState({
          displayState: {
            view: view
          }
        }))
      }))
  }, {dispatch: true});

  successCoreSettings = createEffect(() => {
    return this.actions$.pipe(
      ofType(successCoreSettings),
      takeUntil(this.destroy$),
      switchMap((propVal) => {
          return [
            callPoolsMenu(),
            callPoolsHeadline(), // it was called instantly before and waited for the settings promise
          ]
        }
      ))
  }, {dispatch: true});

  callSportsStats = createEffect(() => {
    return this.actions$.pipe(
      ofType(callSportsStats),
      exhaustMap((propVal) => {
        // Loading state is handled by reducer on callSportsStats action
        return this._poolClient.getStats(propVal.poolId)
          .pipe(
            map((data: SportsStats[]) => {
              return successSportsStats({stats: data});
            }),
            catchError((error) => {
              console.error('[Pool Effects] Failed to load sports stats:', error);
              // Return success with undefined to gracefully degrade - stats are optional UI enhancement
              return of(successSportsStats({stats: undefined}));
            })
          )
      })
    )
  }, {dispatch: true})

  callFantasyStats = createEffect(() => {
    return this.actions$.pipe(
      ofType(callFantasyStats),
      exhaustMap((propVal) => {
        // Loading state is handled by reducer on callFantasyStats action
        return this.fantasyStatsExternalClient.get(propVal.poolId)
          .pipe(
            map((data: FantasyStats[]) => {
              return successFantasyStats({ stats: data });
            }),
            catchError((error) => {
              console.error('[Pool Effects] Failed to load fantasy stats:', error);
              // Return success with undefined to gracefully degrade - stats are optional UI enhancement
              return of(successFantasyStats({ stats: undefined }))
            })
          )
      })
    )
  }, {dispatch: true})

  ///// LIVE UPDATE
  setWebsocketEvent = createEffect(() => {
    return this.actions$.pipe(
      ofType(setWebsocketEvent),
      filter(propVal => ws.RELOAD_EVENTS.concat(Object.keys(ws)).includes(propVal.event)),
      withLatestFrom(this.store.select(selectPools), this.store.select(selectConfiguration)),
      switchMap(([propVal, poolsState, configuration]) => {
        if (!configuration.coreSettings) return EMPTY;
        
        if (ws.RELOAD_EVENTS.includes(propVal.event)) {
          try {
            const hasValidPoolId = Boolean(poolsState.currentPool?.id);
            const isObjectIdMatch = propVal.data?.object_id !== undefined
              ? Number(poolsState.currentPool?.id) === Number(propVal.data.object_id)
              : false;

            // we will update only the pool that's in view for these events
            if ((propVal.event === 'UNIT_DISTRIBUTION_CHANGED' || propVal.event === 'POOL_UPDATED') && hasValidPoolId && isObjectIdMatch) {
              return [
                (poolsState.currentPool?.id ? callPool({id: poolsState.currentPool.id, shouldLoadStats: false}) : null),
              ].filter(action => !!action);
            }

            return [
              callPoolsMenu(),
              callPoolsHeadline(),
              (poolsState.currentPool?.id ? callPool({id: poolsState.currentPool.id, shouldLoadStats: false}) : null),
            ].filter(action => !!action);

          } catch (error) {
            console.error('[Pool Effects] WSPE-01 - Failed updating pools, reloading all:', error);
            // if fails, we just reload them all
            return [
              callPoolsMenu(),
              callPoolsHeadline(),
              (poolsState.currentPool?.id ? callPool({id: poolsState.currentPool.id}) : null),
            ].filter(action => !!action);

          }
        }

        return of(partialUpdate({
          data: propVal.data,
          event: propVal.event
        }))
      })
    )
  }, {dispatch: true})

  isMobile: boolean;
  rootUrl: string;

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private _config: Config,
    private _poolClient: PoolClient,
    private ngrxLocalStorage: LocalStorageService,
    private fantasyStatsExternalClient: FantasyStatsExternalClient
  ) {
    this.isMobile = this._config.CONFIG.isMobile;
    this.rootUrl = this._config.CONFIG.rootUrl;
  }


  statsPreload(pool: any): void {
    // if (pool.sport_code === 'FOOTBALL' && pool.type_code !== 'MATCH') {
    //   // load sport stats
    //   this.store.dispatch(callSportsStats({poolId: pool.id}));
    // }
    if (pool.sport_code === 'FANTASY_GAMES') {
      // load fantasy stats
      this.store.dispatch(callFantasyStats({poolId: pool.id}));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // filter pools


  private filterMenuByState(menuData: any, us_state: string | undefined): PoolsMenuScope.Response {
    if (!us_state) {
      console.warn('No us_state available when filtering menu');
      return menuData;
    }

    // If menuData doesn't have pools array, return as-is
    if (!menuData?.pools || !Array.isArray(menuData.pools)) {
      console.warn('Menu data does not contain pools array');
      return menuData;
    }

    // Get the allowed states from environment
    const allowedStates = this._config.CONFIG.filteredPoolsStates || [];
    const filteredPoolCode = this._config.CONFIG.filteredPoolRuleCode;

    if (allowedStates.length === 0 || !filteredPoolCode) {
      console.warn('No filtering as no states or name_code set');
      return menuData;
    }

    // Trim whitespace from us_state
    const trimmedState = us_state.trim();

    // Filter the pools array
    const filteredPools = menuData.pools.filter((pool: any) => {
      // If rule_code is 'FILTERED' and user state is NOT in allowed states, exclude it
      if (pool.name_code === filteredPoolCode && allowedStates.includes(trimmedState)) {
        console.log('Filtering out pool (id:', pool.id, ') - state', trimmedState, 'in not allowed states');
        return false;
      }

      // Keep all other pools (rule_code is null, or not 'TEST', or state is in allowed list)
      return true;
    });

    // Return the menu data with filtered pools
    return {
      ...menuData,
      pools: filteredPools
    };
  }
}



